// src/features/crypto-info/CryptoInfoPage.tsx
//
// Crypto Info Deep-Dive Page — Component Islands architecture for progressive
// loading and failure isolation. Extracts the base ticker from the URL query
// param (?symbol=BTC), fetches the aggregated Overview (live quote + static
// description + server-computed swing-trading breakout alert) from
// GET /api/v1/crypto-info/{symbol}/overview, and renders an institutional-grade
// header, KPI widgets, the intraday chart, the breakout widget, and the
// network/strategy profile blocks.
//
// Accessed via the "More Info…" action in the Crypto Ranking detail popup.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Layout, Sidebar } from '../../shared/ui';
import { fetchCryptoOverview, type CryptoOverview } from './api';
import { BreakoutAlertWidget } from './components/BreakoutAlertWidget';
import { CryptoIntradayChart } from './components/CryptoIntradayChart';
import { CryptoPriceVolumeChart } from './components/CryptoPriceVolumeChart';
import { usePortfolioStore } from '../user-portfolios/store/usePortfolioStore';

export const CryptoInfoPage = () => {
    const navigate = useNavigate();
    // Zustand: opens the global "Add to Portfolio" modal (isolated re-render).
    const openModal = usePortfolioStore((s) => s.openModal);
    const [searchParams] = useSearchParams();
    const symbol = searchParams.get('symbol')?.toUpperCase();

    const [overview, setOverview] = useState<CryptoOverview | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!symbol) return;
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchCryptoOverview(symbol)
            .then((data) => {
                if (!cancelled) setOverview(data);
            })
            .catch((err) => {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    setError(
                        `Unable to load data for ${symbol}. Please try again.`,
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    const formatCurrency = (val?: number) =>
        val != null
            ? new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: val < 1 ? 6 : 2,
              }).format(val)
            : 'N/A';
    const formatCompact = (val?: number) =>
        val != null
            ? new Intl.NumberFormat('en-US', {
                  notation: 'compact',
                  maximumFractionDigits: 2,
              }).format(val)
            : 'N/A';
    const formatPercent = (val?: number) =>
        val != null ? `${val > 0 ? '+' : ''}${val.toFixed(2)}%` : 'N/A';

    if (!symbol) {
        return (
            <Layout sidebar={<Sidebar />} title="Crypto Intelligence">
                <div className="p-6 text-slate-500 dark:text-slate-400">
                    No symbol provided. Please select an asset from the Crypto
                    Rankings.
                </div>
            </Layout>
        );
    }

    return (
        <Layout
            sidebar={<Sidebar />}
            title={`Deep Dive: ${symbol}`}
            subtitle={
                overview?.quote.name || 'Advanced Institutional Analysis'
            }
        >
            <div className="mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                    &larr; Back to Rankings
                </button>
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse">
                    Loading blockchain quantitative data for {symbol}…
                </div>
            ) : (
                overview && (
                    <div className="space-y-6">
                        {/* --- HEADER ISLAND: Identity & Live Quote --- */}
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 border-l-4 border-l-orange-500">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                                <div>
                                    <div className="flex items-center gap-3 mb-1">
                                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                                            {overview.quote.name || symbol}
                                        </h1>
                                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-mono font-bold rounded border border-slate-200 dark:border-slate-700">
                                            CRYPTO : {symbol}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-4 md:mt-0 text-left md:text-right">
                                    <div className="text-4xl font-mono font-bold text-slate-900 dark:text-white">
                                        {formatCurrency(overview.quote.price)}
                                    </div>
                                    <div
                                        className={`text-lg font-medium mt-1 flex items-center md:justify-end gap-1 ${
                                            (overview.quote.change ?? 0) >= 0
                                                ? 'text-emerald-600 dark:text-emerald-400'
                                                : 'text-red-600 dark:text-red-400'
                                        }`}
                                    >
                                        {(overview.quote.change ?? 0) >= 0
                                            ? '▲'
                                            : '▼'}
                                        {Math.abs(
                                            overview.quote.change ?? 0,
                                        ).toFixed(2)}{' '}
                                        (
                                        {formatPercent(
                                            overview.quote.changes_percentage,
                                        )}
                                        )
                                    </div>
                                    <button
                                        onClick={() =>
                                            openModal({
                                                // FMP live quotes require the USD-suffixed pair
                                                // (BTCUSD, not BTC), otherwise the Polars quote
                                                // join misses and Market Value defaults to $0.
                                                symbol: symbol.endsWith('USD')
                                                    ? symbol
                                                    : `${symbol}USD`,
                                                name: overview.quote.name || symbol,
                                                type: 'CRYPTO',
                                                currentPrice: overview.quote.price ?? 0,
                                            })
                                        }
                                        className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition"
                                    >
                                        + Add to Portfolio
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* --- KPI WIDGETS --- */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    Market Cap
                                </p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    ${formatCompact(overview.quote.market_cap)}
                                </div>
                            </div>
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    24h Volume
                                </p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    ${formatCompact(overview.quote.volume)}
                                </div>
                            </div>
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    24h High
                                </p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    {formatCurrency(overview.quote.day_high)}
                                </div>
                            </div>
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    24h Low
                                </p>
                                <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                    {formatCurrency(overview.quote.day_low)}
                                </div>
                            </div>
                        </div>

                        {/* --- CHARTS & ALERTS GRID --- */}
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch">
                            <div className="xl:col-span-2">
                                <CryptoIntradayChart symbol={symbol} />
                            </div>
                            <div className="xl:col-span-1 h-[440px]">
                                <BreakoutAlertWidget
                                    alert={overview.breakout_alert}
                                />
                            </div>
                        </div>

                        {/* --- PROFILE TEXT BLOCKS --- */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
                                    Network Overview
                                </h2>
                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                    {overview.profile.description ||
                                        'No network description available.'}
                                </p>
                            </div>
                            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm">
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
                                    Fundamental Strategy
                                </h2>
                                <ul className="list-disc list-inside text-sm text-slate-600 dark:text-slate-300 space-y-2 leading-relaxed">
                                    {overview.profile.how_to_trade ? (
                                        overview.profile.how_to_trade
                                            .split(';')
                                            .map((point, idx) => (
                                                <li key={idx}>{point.trim()}</li>
                                            ))
                                    ) : (
                                        <li>No manual strategy defined.</li>
                                    )}
                                </ul>
                            </div>
                        </div>

                        {/* --- ADVANCED PRICE / VOLUME CANDLESTICK CHART --- */}
                        <CryptoPriceVolumeChart symbol={symbol} />
                    </div>
                )
            )}
        </Layout>
    );
};

export default CryptoInfoPage;
